import type { StakeState, StakeType } from './type'
import { formatUnits, parseUnits } from 'ethers'

import { defineStore } from 'pinia'
import { formatTime, getStorage, setStorage } from '@/utils'
import { useAccountStore } from './account'
import { config } from './contract'

export const fee = 15
export const useStakeStore = defineStore('stake', {
  state: (): StakeState => ({
    isModalVisible: false,
    type: 'stake',

    instantUnstakeLimit: '',
    transaction: {
      status: '',
      error: '',
    },
    txData: {
      type: 'stake',
      account: '',
      amount: '',
      transferAmount: '',
      hash: '',
      chainId: 0,
      exploreUrl: '',
      createdAt: '',
    },
    txList: getStorage('txList') || [],
    unstakeNonceList: [],
  }),
  getters: {},
  actions: {
    async toggleModalVisible() {
      this.isModalVisible = !this.isModalVisible
    },
    async toggleStakeType() {
      this.type = this.type === 'stake' ? 'unstake' : 'stake'
    },
    async getInstantUnstakeLimit() {
      try {
        const response = await fetch(`${import.meta.env.VITE_UNSTAKE_WITHDRAW_LIMIT_URL}/graphql`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': '*/*',
          },
          body: JSON.stringify({
            query: `query getCurrentWithdrawLimit {
              getCurrentWithdrawLimit
            }`,
            variables: {},
            operationName: 'getCurrentWithdrawLimit',
          }),
        })

        const data = await response.json()
        const limit = data.data.getCurrentWithdrawLimit
        if (limit) {
          const normalLimit = formatUnits(limit, 18)
          this.instantUnstakeLimit = Number(normalLimit) < 100 ? '0' : String(normalLimit)
        }
        else {
          console.error('Error fetching instant unstake limit:', data)
          this.instantUnstakeLimit = ''
        }
      }
      catch (error) {
        console.error('Error fetching instant unstake limit:', error)
        this.instantUnstakeLimit = ''
      }
    },

    changeTransactionStatus(transaction: { status: string, error: string }) {
      this.transaction = transaction
    },
    changeTxList(txList: any) {
      this.txList = txList
    },
    addTxList(txData: any) {
      this.txList.unshift(txData)
      setStorage('txList', this.txList)
    },
    resetTxData() {
      this.txData = {
        type: 'stake',
        account: '',
        amount: '',
        transferAmount: '',
        hash: '',
        chainId: 0,
        exploreUrl: '',
        createdAt: '',
      }
    },
    changeUnstakeNonceList(unstakeNonceList: any) {
      this.unstakeNonceList = unstakeNonceList
    },

    async checkAllowance(amount: string, type: 'stake' | 'unstake' | 'bridge') {
      const accountStore = useAccountStore()
      const contractHelper = accountStore.contractHelper
      const isCcip = contractHelper?.adapter.isCcip
      const chain = isCcip ? 'L2' : 'L1'
      const tokenAddress
        = type === 'stake' && isCcip
          ? config[chain]?.contracts.WASTR
          : config[chain]?.contracts.WstASTR
      console.log(tokenAddress, 'tokenAddress')
      const toAddress
        = type === 'unstake' && !isCcip ? config.L1?.contracts.deploy.Vault : undefined
      const allowance = await contractHelper?.checkAllowance(
        tokenAddress,
        accountStore.account as string,
        toAddress,
      )
      console.log(allowance, amount, 'allowance2')
      return Number(allowance) < Number(amount)
    },

    async approveToken(amount: string, type: 'stake' | 'unstake' | 'bridge') {
      const accountStore = useAccountStore()
      const contractHelper = accountStore.contractHelper
      const isCcip = contractHelper?.adapter.isCcip
      const chain = isCcip ? 'L2' : 'L1'
      const tokenAddress
        = type === 'stake' && isCcip
          ? config[chain]?.contracts.WASTR
          : config[chain]?.contracts.WstASTR

      const toAddress
        = type === 'unstake' && !isCcip ? config.L1?.contracts.deploy.Vault : undefined

      try {
        this.transaction.status = 'pending'
        this.type = 'approve'
        const transactionReceipt = await contractHelper?.approveToken(
          tokenAddress,
          amount,
          toAddress,
        )
        this.transaction.status = ''
        return transactionReceipt
      }
      catch (error) {
        this.transaction.status = 'error'
        this.transaction.error = error instanceof Error ? error.message : String(error)
      }
    },

    // async getUnstakeNonceList() {
    //   const accountStore = useAccountStore()
    //   const contractHelper = accountStore.contractHelper
    //   const list_ = await contractHelper?.getUnstakeNonceList(accountStore.account as string)
    //   const list = list_.filter((item: any) => item.pendingUnstake !== 0)
    //   const needNewTime = 720 * 3 + 3600 * 4

    //   const promises = list.map((item: any) => {
    //     const blockNumber = item.pendingUnstakeAt

    //     // 确保区块号在有效范围内
    //     try {
    //       return getBlock(wagmiAdapter.wagmiConfig, {
    //         blockNumber: BigInt(blockNumber), // 直接使用 BigInt 而不是 parseUnits
    //       })
    //         .then((blockInfo) => {
    //           const blockTimestamp = Number(blockInfo.timestamp) + needNewTime
    //           const blockTime = formatTime(blockTimestamp * 1000)
    //           return {
    //             ...item,
    //             blockTime,
    //           }
    //         })
    //         .catch((error) => {
    //           console.log(error, 'error')
    //           return {
    //             ...item,
    //             blockTime: undefined,
    //           }
    //         })
    //     }
    //     catch (error) {
    //       console.error('Invalid block number:', blockNumber, error)
    //       return Promise.resolve({
    //         ...item,
    //         blockTime: undefined,
    //       })
    //     }
    //   })
    //   this.unstakeNonceList = await Promise.all(promises)
    // },

    async checkAmount(amount: string, type: StakeType) {
      const accountStore = useAccountStore()
      const contractHelper = accountStore.contractHelper
      let shares
      if (type === 'stake') {
        shares = await contractHelper?.convertToShares(amount)
      }
      else {
        shares = await contractHelper?.convertToAssets(amount)
      }

      this.txData.transferAmount = shares || ''
      console.log(shares, 'shares')
      return shares
    },

    changeTxData(txData: any) {
      this.txData = txData
    },
    async stakeOrUnstakeAction(amount: string, type: StakeType) {
      const accountStore = useAccountStore()
      const account = accountStore.account
      const contractHelper = accountStore.contractHelper

      console.log(account, amount, 'account')
      const getTransactionType = (type: StakeType, isCcip: boolean) => {
        switch (type) {
          case 'stake':
            return 'stake'
          case 'unstake':
            return isCcip ? 'bridge' : 'withdraw'
          default:
            return type
        }
      }
      let result

      try {
        if (type === 'stake') {
          result = await contractHelper?.stake(account as string, amount)
        }
        else {
          result = await contractHelper?.unstake(account as string, amount)
        }
        if (result.transactionHash) {
          const exploreUrlPrefix = contractHelper?.exploreUrlPrefix
          const isCcip = exploreUrlPrefix?.includes('ccip') || false

          let messageId
          if (isCcip) {
            messageId = await contractHelper?.getMessageId(result.transactionHash)
          }
          this.transaction.status = 'success'
          let exploreUrl
            = (contractHelper?.exploreUrlPrefix || '') + (isCcip ? account : result.transactionHash)
          if (messageId) {
            exploreUrl = `${exploreUrl}#/side-drawer/msg/${messageId}`
          }

          const txData = {
            ...this.txData,
            hash: result.transactionHash,
            type: getTransactionType(type, isCcip),
            exploreUrl,
            amount,
            chainId: accountStore.currentNetworkChainId,
            createdAt: formatTime(new Date().getTime()),
            messageId,
            account,
          }

          Promise.all([
            this.changeTxData(txData),
            isCcip || type === 'unstake' ? this.addTxList(txData) : Promise.resolve(),
            // this.getUnstakeNonceList(),
            accountStore.getUserInfo(),
          ]).catch(console.error)

          return txData
        }
        else {
          this.transaction.status = 'error'
          this.transaction.error = result.error
        }
      }
      catch (error) {
        this.transaction.status = 'error'
        this.transaction.error = error instanceof Error ? error.message : String(error)
      }
    },
    async instantUnstakeAction(amount: string) {
      const accountStore = useAccountStore()
      const account = accountStore.account
      const contractHelper = accountStore.contractHelper

      console.log(account, amount, 'account')

      try {
        const result = await contractHelper?.instantUnstake(account as string, amount)
        if (result.transactionHash) {
          try {
            // 更新instantUnstakeLimit
            const response = await fetch(`${import.meta.env.VITE_UNSTAKE_WITHDRAW_LIMIT_URL}/graphql`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': '*/*',
              },
              body: JSON.stringify({
                query: `mutation UpdateInstanceWithdrawLimit($num: String!) {
                    updateInstanceWithdrawLimit(num: $num)
                  }`,
                variables: {
                  num: parseUnits(amount, 18).toString(),
                },
                operationName: 'UpdateInstanceWithdrawLimit',
              }),
            })
            const data = await response.json()
            if (!data.data?.updateInstanceWithdrawLimit) {
              console.error('Failed to update instant withdraw limit:', data)
            }
            await this.getInstantUnstakeLimit()
          }
          catch (error) {
            console.error('Error updating instant withdraw limit:', error)
          }
          const exploreUrlPrefix = contractHelper?.exploreUrlPrefix
          const isCcip = exploreUrlPrefix?.includes('ccip')
          this.transaction.status = 'success'
          let messageId
          if (isCcip) {
            messageId = await contractHelper?.getMessageId(result.transactionHash)
          }

          let exploreUrl
            = (contractHelper?.exploreUrlPrefix || '') + (isCcip ? account : result.transactionHash)
          if (messageId) {
            exploreUrl = `${exploreUrl}#/side-drawer/msg/${messageId}`
          }
          const txData = {
            ...this.txData,
            hash: result.transactionHash,
            type: 'instantUnstake',
            exploreUrl,
            amount,
            chainId: accountStore.currentNetworkChainId,
            createdAt: formatTime(new Date().getTime()),
            messageId,
            account,
          }
          this.changeTxData(txData)
          this.addTxList(txData)

          Promise.all([
            // this.getUnstakeNonceList(),
            accountStore.getUserInfo(),
          ]).catch(console.error)
          return txData
        }
        else {
          this.transaction.status = 'error'
          this.transaction.error = result.error
        }
      }
      catch (error) {
        this.transaction.status = 'error'
        this.transaction.error = error instanceof Error ? error.message : String(error)
      }
    },
  },
})
